import { ExpandMore, MoreHorizOutlined } from "@mui/icons-material";
import {
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  TextField,
  Button,
  Typography,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SectionHeader } from "../../Forms/SectionHeader";
import WorkflowSessionModal from "../../WorkflowSessionModal";
import { debounce, set } from "lodash";
import { typesOfQos } from "../../../../constants/constantList";
import { useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";

function QualityOfService({ connectionDetails, updateAndSaveData, showEnhancedQOS = false, screen }) {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const errorHandlerList = rightTree?.tree?.templateHeader?.WORKFLOW?.ERROR_HANDLER_LIST || [];
  const [qualityOfService, setQualityOfService] = useState(false);
  const [noOfRetries, setNoOfRetries] = useState("");
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(1);
  const [useSessionData, setUseSessionData] = useState(false);
  const [sessionData, setSessionData] = useState('');
  const [valueLabel, setValueLabel] = useState('');
  const [error, setError] = useState(false);
  const [workflowSessionModal, setWorkflowSessionModal] = useState({
    open: false,
    isFirstTime: false,
  });

  // Enhanced QOS
  const [qosType, setQosType] = useState("");
  const [notifyFailureAfter, setNotifyFailureAfter] = useState("");
  const [errorHandlerId, setErrorHandlerId] = useState("");
  const [httpCodes, setHttpCodes] = useState("");
  const [errorHandlerListOptions, setErrorHandlerListOptions] = useState([{
    text: t("SELECT"), value: t("SELECT")
  }]);

  const handleChange = (value, setState, key) => {
    if (((key === "RETRY_COUNT" && value.length > noOfRetries.length)
      || (key === 'TIME_BEFORE_RETRY' && value.length > sessionData.length))
      && value.length > 10)
      return;
      
    setState(value);
    debounceUpdateAndSave({[key]: value})
  };

  const handleQOSChange = (checked) => {
    setQualityOfService(checked)
    if (!checked) {
      // reset all controls
      setHours(0);
      setMinutes(0);
      setSeconds(1);
      setNoOfRetries("");
      setUseSessionData(false);
      setSessionData("");

      // delete data from JSON
      const modifyProps = {
        TIME_BEFORE_RETRY: null,
        RETRY_COUNT: null,
        QOS_HTTP_CODES: null,
      };
      debounceUpdateAndSave({ remove: modifyProps });
    } else {
      // reset all controls
      setHours(0);
      setMinutes(0);
      setSeconds(1);
      setUseSessionData(false);
      setSessionData("");
      debounceUpdateAndSave({ TIME_BEFORE_RETRY: "1" });
    }
  };
  
  const handleHoursChange = (e) => {
    const { value } = e.target
    const numValue = Number(value);
    const updatedTotalSeconds = seconds + (minutes*60) + (numValue * 3600);
    
    // If total time becomes 0, ensure at least 1 second
    if (updatedTotalSeconds === 0) {
      setHours(numValue);
      setSeconds(1);
      debounceUpdateAndSave({TIME_BEFORE_RETRY: '1'});
    } else {
      setHours(numValue);
      debounceUpdateAndSave({TIME_BEFORE_RETRY: updatedTotalSeconds + ''});
    }
  };

  const handleMinutesChange = (e) => {
    const { value } = e.target;
    const numValue = Number(value);
    const updatedTotalSeconds = seconds + (numValue * 60) + (hours * 3600);
    
    // If total time becomes 0, ensure at least 1 second
    if (updatedTotalSeconds === 0) {
      setMinutes(numValue);
      setSeconds(1);
      debounceUpdateAndSave({TIME_BEFORE_RETRY: '1'});
    } else {
      setMinutes(numValue);
      debounceUpdateAndSave({TIME_BEFORE_RETRY: updatedTotalSeconds + ''});
    }
  };

  const handleSecondsChange = (e) => {
    const { value } = e.target;
    
    if (value === "") {
      setSeconds(""); 
      setError(false);
      return;
    }
  
    const numValue = Number(value);
    const updatedTotalSeconds = numValue + (minutes * 60) + (hours * 3600);
    
    // Allow 0 seconds if there are non-zero hours or minutes, otherwise require at least 1 second
    const hasOtherTimeComponents = hours > 0 || minutes > 0;
    const minSeconds = hasOtherTimeComponents ? 0 : 1;
    
    if (numValue < minSeconds) {
      setError(true);
      setSeconds("")
    } else {
      setError(false);
      setSeconds(numValue);
      debounceUpdateAndSave({TIME_BEFORE_RETRY: updatedTotalSeconds + ''})
    }
  };
  const handleSecondsBlur = () => {
    const hasOtherTimeComponents = hours > 0 || minutes > 0;
    const minSeconds = hasOtherTimeComponents ? 0 : 1;
    
    if (!seconds || seconds < minSeconds) {
      setSeconds(minSeconds);
      setError(false);
    } else if (seconds > 59) {
      setSeconds(getValidNumbers(seconds));
    }
  };
  const handleMinutesBlur = () => {
    if(!minutes){
        setMinutes(0);
    } else if(minutes > 59) {
        setMinutes(getValidNumbers(minutes));
    }
    
    // After minutes adjustment, check if total time is 0
    const totalSeconds = seconds + (minutes * 60) + (hours * 3600);
    if (totalSeconds === 0) {
      setSeconds(1);
      debounceUpdateAndSave({TIME_BEFORE_RETRY: '1'});
    }
  }
  const handleHoursBlur = () => {
    const getValidHours = (num) => {
        const strNum = num.toString();
        let validNumber = 0;
    
        for (let i = 1; i <= strNum.length; i++) {
          const sliced = Number(strNum.slice(0, i));
    
          if (sliced >= 1 && sliced <= 23) {
            validNumber = sliced;
          }
        }
    
        return validNumber;
    }
    if(!hours){
      setHours(0);
    } else if(hours > 23){
        setHours(getValidHours(hours));
    }
    
    // After hours adjustment, check if total time is 0
    const totalSeconds = seconds + (minutes * 60) + (hours * 3600);
    if (totalSeconds === 0) {
      setSeconds(1);
      debounceUpdateAndSave({TIME_BEFORE_RETRY: '1'});
    }
  }
  const getValidNumbers = (num) => {
    const strNum = num.toString();
    let validNumber = 1;

    for (let i = 1; i <= strNum.length; i++) {
      const sliced = Number(strNum.slice(0, i));

      if (sliced >= 1 && sliced <= 59) {
        validNumber = sliced;
      }
    }

    return validNumber;
  };
  const valuesHandler = (value) => {
    if (valueLabel === t("LBL_NUM_RETRIES")) {
      setNoOfRetries(value);
      debounceUpdateAndSave({RETRY_COUNT: value});
    } else if (valueLabel === t("LBL_QOS_HTTP_CODES")) {
      setHttpCodes(value);
      debounceUpdateAndSave({QOS_HTTP_CODES: value});
    } else {
      setSessionData(value);
      debounceUpdateAndSave({TIME_BEFORE_RETRY: value});
    }
  }
  const debounceUpdateAndSave = debounce(updateAndSaveData, 300);
  
  useEffect(() => {
   if(connectionDetails){
    const getCorrectRetryInterval = (value) => {
      if(value) {
        const numValue = Number(value);
        if(!isNaN(numValue)){
           const hrs = Math.floor(numValue / 3600);
           const min = Math.floor((numValue % 3600) / 60);
           const sec = numValue % 60;
           
           setHours(hrs);
           setMinutes(min);
           setSeconds(sec);
           setUseSessionData(false);
           setSessionData('')
        } else {
          setUseSessionData(true);
          setSessionData(value)
          setHours(0)
          setMinutes(0);
          setSeconds(1)
        }
      } 
     }

     setQualityOfService(((connectionDetails?.RETRY_COUNT || connectionDetails?.TIME_BEFORE_RETRY) || 
     (connectionDetails?.GUARANTY_DELIVERY === "TRUE" || connectionDetails?.GUARANTY_DELIVERY === "true")) ? true : false);
     setNoOfRetries(connectionDetails?.RETRY_COUNT || '');
     getCorrectRetryInterval(connectionDetails?.TIME_BEFORE_RETRY || '' )

     // Enhanced QOS
      setNotifyFailureAfter(connectionDetails?.ALERT || '');
      setErrorHandlerId(connectionDetails?.ERROR_HANDLER_ID || '');
      setHttpCodes(connectionDetails?.QOS_HTTP_CODES || '');
   }
  },[connectionDetails?.RETRY_COUNT, connectionDetails?.TIME_BEFORE_RETRY]);

  useEffect(() => {
    if(showEnhancedQOS){
      if(connectionDetails?.GUARANTY_DELIVERY === "TRUE" || connectionDetails?.GUARANTY_DELIVERY === "true"){
        setQosType(t("LBL_GUARANTEED_DELIVERY"));
      } else if(connectionDetails?.hasOwnProperty("RETRY_COUNT") || connectionDetails?.hasOwnProperty("TIME_BEFORE_RETRY")){
        setQosType(t("LBL_RETRY_LOGIC"));
      } else 
       setQosType(t("SELECT"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEnhancedQOS]);

  useEffect(() => {
    if(errorHandlerList.length > 0) {
      const options = errorHandlerList.map((item) => ({
        text: item?.OUTBOUND_NAME || "",
        value: item?.ID || ""
      }));
      setErrorHandlerListOptions([{
        text: t("SELECT"), value: t("SELECT")
      }, ...options]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorHandlerList]);
  
  return (
    <div>
      <Accordion sx={styles.accordianContainer} defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="accordian-override"
          sx={styles.accordianHeader}>
          <SectionHeader text={t("LBL_QOS")} />
        </AccordionSummary>
        <AccordionDetails sx={styles.accordianContainer}>
          {/* QOS Checkbox */}
          <FormControlLabel
            label={t("LBL_QOS")}
            sx={{ height: "30px" }}
            className="width-fit-content"
            style={{ marginBottom: "10px" }}
            control={
              <Checkbox
                checked={qualityOfService}
                size="small"
                name="QOS"
                onChange={(e, checked) => handleQOSChange(checked)}
              />
            }
          />

          {qualityOfService && (
            <>
              {showEnhancedQOS && <FormControl
                variant="outlined"
                className="width-100"
                size="small"
                style={{
                  marginBottom: "10px",
                }}>
                <InputLabel
                >
                  {t("LBL_TYPE_OF_QOS")}
                </InputLabel>
                <Select
                  variant="outlined"
                  size="small"
                  className={"width-100"}
                  value={qosType || t("SELECT")}
                  onChange={(e) => {
                    setQosType(e.target.value);
                    if(e.target.value === t("LBL_GUARANTEED_DELIVERY")){
                      debounceUpdateAndSave({add: { GUARANTY_DELIVERY: "TRUE", RETRY_COUNT: null}});
                    } else
                      debounceUpdateAndSave({ GUARANTY_DELIVERY: null});
                  }}
                >
                  {typesOfQos.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>))}
                </Select>
              </FormControl>}

              {/* HTTP Codes field - show for retry logic in enhanced QOS OR for GET_DATA/SEND_DATA */}
              {(connectionDetails?.ADAPTER_TYPE === "HTTP" && (qosType === t("LBL_RETRY_LOGIC") || screen === "GET_DATA"))
              && (
                <div style={{ display: "flex", gap: "4px", marginBottom: "10px" }}>
                  <FormControl className="width-100" size="small">
                    <TextField
                      variant="outlined"
                      size="small"
                      label={t("LBL_QOS_HTTP_CODES")}
                      value={httpCodes}
                      className="width-100"
                      onChange={(e) => handleChange(e.target.value, setHttpCodes, 'QOS_HTTP_CODES')}
                      autoComplete="off"
                    />
                  </FormControl>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ width: "40px", minWidth: "40px" }}
                    onClick={() => {
                      setValueLabel(t("LBL_QOS_HTTP_CODES"));
                      setWorkflowSessionModal({
                        open: true,
                        isFirstTime: true,
                      });
                    }}>
                    <MoreHorizOutlined fontSize="small" />
                  </Button>
                </div>
              )}

              <div style={{ marginTop: "5px" }}>
                {/* Enhanced QOS - Notify Failure */}
                {(showEnhancedQOS && qosType === t("LBL_GUARANTEED_DELIVERY")) &&
                  <FormControl className="width-100" size="small" 
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      marginBottom: "10px",
                      gap: "4px",
                      justifyContent: "space-between"
                    }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      label={t("FRG_NOTIFY_OF_FAILURE_AFTER")}
                      className="width-100"
                      value={notifyFailureAfter || ""}
                      onChange={(e) => handleChange(e.target.value || null, setNotifyFailureAfter, 'ALERT')}
                      autoComplete="off"
                      type="number"
                    />
                    <Typography
                      style={{ marginLeft: "5px" }} className="width-fit-content">
                      {t("ATTEMPTS")}
                    </Typography>
                  </FormControl>
                }

                {/* # of retries */}
                { (!showEnhancedQOS || (showEnhancedQOS && qosType === t("LBL_RETRY_LOGIC"))) && <div style={{ display: "flex", gap: "4px" }}>
                  <FormControl className="width-100" size="small">
                    <TextField
                      variant="outlined"
                      size="small"
                      label={t("LBL_NUM_RETRIES")}
                      value={noOfRetries}
                      className="width-100"
                      onChange={(e) => handleChange(e.target.value, setNoOfRetries, 'RETRY_COUNT')}
                      autoComplete="off"
                    />
                  </FormControl>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ width: "40px", minWidth: "40px" }}
                    onClick={() => {
                      setValueLabel(t("LBL_NUM_RETRIES"));
                      setWorkflowSessionModal({
                        open: true,
                        isFirstTime: true,
                      });
                    }}>
                    <MoreHorizOutlined fontSize="small" />
                  </Button>
                </div> }
                
                { (!showEnhancedQOS || (showEnhancedQOS && qosType !== t("SELECT"))) && 
                <div>
                  <Typography
                    style={{ marginBottom: "10px", marginTop: "10px" }}>
                    {t("LBL_RETRY_INTERVAL")}
                  </Typography>
                  <div style={{ display: "flex", gap: "3px" }}>
                    <TextField
                      size="small"
                      name="hours"
                      variant="outlined"
                      label={t("LBL_HOURS")}
                      value={hours}
                      disabled={useSessionData}
                      onChange={handleHoursChange}
                      onBlur={handleHoursBlur}
                      type="number"
                      inputProps={{ min: 0, max: 23 }}
                      InputProps={{ sx: { height: "34px !important" } }}
                      sx={{ width: "unset", minWidth: "110px", flex: "1" }}
                    />
                    <TextField
                      size="small"
                      name="min"
                      variant="outlined"
                      label={t("LBL_MINUTES")}
                      value={minutes}
                      disabled={useSessionData}
                      onChange={handleMinutesChange}
                      onBlur={handleMinutesBlur}
                      type="number"
                      inputProps={{ min: 0, max: 59 }}
                      InputProps={{ sx: { height: "34px !important" } }}
                      sx={{ width: "unset", minWidth: "110px", flex: "1" }}
                    />
                    <TextField
                      size="small"
                      name="sec"
                      variant="outlined"
                      label={t("LBL_GET_DATA_SEC")}
                      value={seconds}
                      disabled={useSessionData}
                      onChange={handleSecondsChange}
                      onBlur={handleSecondsBlur}
                      type="number"
                      error={error}
                      helperText={error ? t('ERR_MIN_VALUE_FOR_QOS_RETRY') : ''}
                      inputProps={{ min: (hours > 0 || minutes > 0) ? 0 : 1, max: 59 }}
                      InputProps={{ sx: { height: "34px !important" } }}
                      sx={{ width: "unset", minWidth: "110px", flex: "1" }}
                    />
                  </div> 
                </div> }
              </div>
              {/* Use Session Data */}
              { (!showEnhancedQOS || (showEnhancedQOS && qosType !== t("SELECT"))) && 
              <div style={{ marginTop: "5px" }}>
                <FormControlLabel
                  label={t("LBL_QOS_USE_SESS_DATA")}
                  sx={{ height: "30px", marginTop: "5px" }}
                  className="width-fit-content"
                  control={
                    <Checkbox
                      checked={useSessionData}
                      size="small"
                      name="USD"
                      onChange={(e, checked) => setUseSessionData(checked)}
                    />
                  }
                />
                {useSessionData && (
                  <div style={{ display: "flex", gap: "4px" }}>
                    <FormControl className="width-100" size="small">
                      <TextField
                        variant="outlined"
                        size="small"
                         label={t("LBL_GET_DATA_SEC")}
                        value={sessionData}
                        className="width-100"
                        onChange={(e) => handleChange(e.target.value, setSessionData, 'TIME_BEFORE_RETRY')}
                        autoComplete="off"
                      />
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ width: "40px", minWidth: "40px" }}
                      onClick={() => {
                        setValueLabel(t('LBL_GET_DATA_SEC'))
                        setWorkflowSessionModal({
                          open: true,
                          isFirstTime: true,
                        })
                      }  
                      }>
                      <MoreHorizOutlined fontSize="small" />
                    </Button>
                  </div>
                )}
              </div> }
              {showEnhancedQOS && qosType === t("LBL_GUARANTEED_DELIVERY") && <FormControl
                variant="outlined"
                className="width-100"
                size="small"
                style={{
                  marginTop: "10px",
                  marginBottom: "10px",
                }}>
                <InputLabel
                >
                  {t("ERROR_HANDLER")}
                </InputLabel>
                <Select
                  variant="outlined"
                  size="small"
                  className={"width-100"}
                  value={errorHandlerId || t("SELECT")}
                  onChange={(e) => {
                    if (e.target.value === t("SELECT")) {
                      setErrorHandlerId(t("SELECT"));
                      return;
                    }
                    
                    setErrorHandlerId(e.target.value);
                    handleChange(e.target.value, setErrorHandlerId, 'ERROR_HANDLER_ID');
                  }}
                >
                  {errorHandlerListOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>))}
                </Select>
              </FormControl>}
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <WorkflowSessionModal
        valueLabel={valueLabel}
        showAccountData={true}
        showCallingWorkflow={true}
        showSystemData={true}
        showWorkflowSession={true}
        openModal={workflowSessionModal}
        setModalOpen={setWorkflowSessionModal}
        handleClose={() =>
          setWorkflowSessionModal({ open: false, isFirstTime: false })
        }
        initialValue={valueLabel === t("LBL_NUM_RETRIES") ? noOfRetries : valueLabel === t("LBL_QOS_HTTP_CODES") ? httpCodes : sessionData}
        valuesHandler={(value) => {
          valuesHandler(value)
        }}
      />
    </div>
  );
}

export default QualityOfService;
const styles = {
  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset",
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset",
  },
  accordianContent: {
    border: "none",
    padding: "0 15px",
    margin: "0",
    backgroundColor: "unset",
  },
};
